// client/src/pages/ShoppingListPage.jsx - Page de liste de courses
import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { getShoppingList } from '../api/shoppingApi';
import { placeOrder } from '../api/orderApi';
import ShoppingList from '../components/ShoppingList';
import Seo from '../components/Seo';

const ShoppingListPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [shoppingList, setShoppingList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('carrefour');

  const metaTitle = shoppingList?.name
    ? `Liste de courses – ${shoppingList.name}`
    : 'Liste de courses';
  const metaDescription = shoppingList?.orderDetails?.provider
    ? `Consultez et suivez la liste de courses générée avec YAQAM pour votre commande ${shoppingList.orderDetails.provider}.`
    : 'Consultez votre liste de courses générée automatiquement par YAQAM pour vos repas personnalisés.';

  const seo = (
    <Seo
      title={metaTitle}
      description={metaDescription}
      breadcrumb
      noindex={!shoppingList}
    />
  );
  
  useEffect(() => {
    const fetchShoppingList = async () => {
      try {
        setLoading(true);
        const response = await getShoppingList(id);
        setShoppingList(response.data.data);
      } catch (error) {
        console.error('Erreur lors de la récupération de la liste de courses:', error);
        setError('Impossible de charger cette liste de courses');
      } finally {
        setLoading(false);
      }
    };
    
    fetchShoppingList();
  }, [id]);
  
  const handleOrderDrive = async () => {
    try {
      setOrderLoading(true);
      setOrderError(null);
      setOrderSuccess(false);
      
      const response = await placeOrder(id, selectedProvider);
      
      setOrderSuccess(true);
      
      // Mettre à jour l'état local
      setShoppingList(prevList => ({
        ...prevList,
        status: 'ordered',
        driveOrderId: response.data.data.orderId,
        orderDetails: {
          provider: selectedProvider,
          date: new Date(),
          status: 'pending',
        },
      }));
    } catch (error) {
      console.error('Erreur lors de la commande au drive:', error);
      setOrderError(
        error.response?.data?.message || 'Erreur lors de la commande au drive'
      );
    } finally {
      setOrderLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    try {
      setLoading(true);
      const response = await getShoppingList(id);
      setShoppingList(response.data.data);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la liste:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <>
        {seo}
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#87A96B] mx-auto mb-4"></div>
            <p className="text-[#2C3E50]">Chargement de la liste de courses...</p>
          </div>
        </div>
      </>
    );
  }
  
  if (error) {
    return (
      <>
        {seo}
        <div className="bg-red-100 text-red-800 p-4 rounded-lg border border-red-200">
          {error}
        </div>
      </>
    );
  }
  
  if (!shoppingList) {
    return (
      <>
        {seo}
        <div className="text-center py-8">
          <p className="text-[#2C3E50] mb-4">Liste de courses non trouvée</p>
          <Link 
            to="/dashboard" 
            className="text-[#2C3E50] hover:text-[#2C3E50]/70 font-medium transition-colors"
          >
            Retour au tableau de bord
          </Link>
        </div>
      </>
    );
  }
  
  const isPremium = ['premium','unlimited'].includes(user.subscription.type);
  const isOrdered = shoppingList.status === 'ordered';
  
  return (
    <>
      {seo}
      <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link
          to={`/meal-plans/${shoppingList.mealPlan}`}
          className="text-[#2C3E50] hover:text-[#2C3E50]/70 flex items-center font-medium transition-colors"
        >
          ← Retour au plan de repas
        </Link>
        
        <h1 className="mt-4 text-3xl font-bold mb-6 text-[#2C3E50]">Liste de courses</h1>
      </div>
      
      <div className="mb-8">
        <ShoppingList
          shoppingList={shoppingList}
          onUpdate={handleRefresh}
        />
      </div>
      
      {/* Section commande au drive */}
      {!isOrdered ? (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100">
          <h2 className="text-xl font-semibold mb-4 text-[#2C3E50]">Commander au drive</h2>
          
          {!isPremium ? (
            <div>
              <p className="text-gray-600 mb-4">
                Cette fonctionnalité est réservée aux utilisateurs Premium.
              </p>
              <Link
                to="/subscription"
                className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#E67E22]/90 transition-colors font-medium"
              >
                Passer Premium
              </Link>
            </div>
          ) : (
            <div>
              <div className="mb-4">
                <label htmlFor="provider" className="block text-[#2C3E50] font-medium mb-2">
                  Choisissez votre drive:
                </label>
                <select
                  id="provider"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#87A96B] focus:border-[#87A96B] transition-colors"
                  disabled={orderLoading}
                >
                  <option value="carrefour">Carrefour Drive</option>
                  <option value="leclerc" disabled>Leclerc Drive (Bientôt disponible)</option>
                  <option value="intermarche" disabled>Intermarché Drive (Bientôt disponible)</option>
                </select>
              </div>
              
              {orderError && (
                <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4 border border-red-200">
                  {orderError}
                </div>
              )}
              
              {orderSuccess && (
                <div className="bg-[#87A96B]/10 text-[#87A96B] p-4 rounded-lg mb-4 border border-[#87A96B]/30">
                  Commande envoyée avec succès au drive {selectedProvider}!
                </div>
              )}
              
              <button
                onClick={handleOrderDrive}
                disabled={orderLoading}
                className="px-4 py-2 bg-[#87A96B] text-white rounded-lg hover:bg-[#87A96B]/90 disabled:bg-gray-400 transition-colors font-medium"
              >
                {orderLoading ? 'Commande en cours...' : 'Commander au Drive'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-[#87A96B]/10 rounded-lg shadow-md p-6 border border-[#87A96B]/30">
          <h2 className="text-xl font-semibold mb-2 text-[#87A96B]">Commande déjà passée</h2>
          <p className="text-[#2C3E50]">
            Cette liste a déjà été commandée chez {shoppingList.orderDetails.provider}.
          </p>
          <div className="mt-4 p-3 bg-white rounded-lg border border-gray-100">
            <div className="flex justify-between text-[#2C3E50]">
              <span className="font-medium">Statut:</span>
              <span className="capitalize">{shoppingList.orderDetails.status}</span>
            </div>
            <div className="flex justify-between text-[#2C3E50]">
              <span className="font-medium">ID de commande:</span>
              <span>{shoppingList.driveOrderId}</span>
            </div>
            <div className="flex justify-between text-[#2C3E50]">
              <span className="font-medium">Date:</span>
              <span>
                {new Date(shoppingList.orderDetails.date).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ShoppingListPage;
